import { Appointment } from '../../firebase/firestore/documents/candidate';

export interface TodoResponse {
  count: TodoStats;
  data: Todo[];
}

export interface TodoStats {
  total: number;
  totalOfferRejected: number;
  totalOfferExpired: number;
  totalOfferMade: number;
  totalOfferAccepted: number;
  totalBackgroundCheck: number;
  totalHireForm: number;
  totalHireFormResubmission: number;
  outsandingOffers: number;
  totalWorkbright: number;
  hireProcessing: number;
  toDo: number;
  completed: number;
  offerMade: OfferMade;
  offerAccepted: OfferAccepted;
  offerRejected: OfferRejected;
  backgroundCheck: BackgroundCheck;
  hireForm: HireForm;
  languageEvaluation: number;
  dialogflow: number;
  pausedCandidates: number;
  workbright: Workbright;
  inProgress: number;
  upcomingInterviews: number;
}

export interface OfferMade {
  offerMade48HoursAgo: number;
  offerMade36HoursAgo: number;
  offerMade24HoursAgo: number;
  offerMade12HoursAgo: number;
}

export interface OfferAccepted {
  offerAccepted48HoursAgo: number;
  offerAccepted36HoursAgo: number;
  offerAccepted24HoursAgo: number;
  offerAccepted12HoursAgo: number;
}

export interface OfferRejected {
  offerRejected48HoursAgo: number;
  offerRejected36HoursAgo: number;
  offerRejected24HoursAgo: number;
  offerRejected12HoursAgo: number;
  offerExpired48HoursAgo: number;
  offerExpired36HoursAgo: number;
  offerExpired24HoursAgo: number;
  offerExpired12HoursAgo: number;
}

export interface BackgroundCheck {
  bgCheckFlaggedByCertnCandidates: number;
  bgCheckNotProcessedByCertn24HoursAgoCandidates: number;
  bgCheckRequested72HoursAgo: number;
  bgCheckRequested48HoursAgo: number;
  bgCheckRequested24HoursAgo: number;
}

export interface HireForm {
  hireFormHelpRequested: number;
  hireFormCompleted24HoursAgo: number;
  hireFormRequested72HoursAgo: number;
  hireFormRequested48HoursAgo: number;
  hireFormRequested24HoursAgo: number;
  hireFormRequested12HoursAgo: number;
  hireFormResubmission72HoursAgo: number;
  hireFormResubmission48HoursAgo: number;
  hireFormResubmission24HoursAgo: number;
  hireFormResubmission12HoursAgo: number;
}

export interface Workbright {
  workbrightAccepted: number;
  workbrightDeclined: number;
  workbrightCompleted: number;
  workbrightRequested72HoursAgo: number;
  workbrightRequested48HoursAgo: number;
  workbrightRequested24HoursAgo: number;
  workbrightRequested36HoursAgo: number;
  workbrightRequested60HoursAgo: number;
  workbrightRestarted72HoursAgo: number;
  workbrightRestarted48HoursAgo: number;
  workbrightRestarted24HoursAgo: number;
  workbrightRestarted36HoursAgo: number;
  workbrightRestarted60HoursAgo: number;
}

export interface Todo {
  id: string;
  lastName: string;
  jobTitle: JobTitle;
  businessName: string;
  dismissed: boolean;
  appointment?: Appointment;
  computedStatus: ComputedStatus;
  positionName: string;
  firstName: string;
  accountExcluded: boolean;
  seekerLookingForWork: boolean;
  hireDetailsStatus?: string;
  hireDetailsRequestedAt?: string;
  seeker?: string;
  forSorting?: string;
  position: string;
  status: string;
  lastUpdate?: string;
  customName: string;
  recruiterActionId: string;
  category: string;
  todoStatus: StatusEnum;
  hireDetailsResubmissionRequestedAt?: string;
  hireDetailsResubmissionRequest?: HireDetailsResubmissionRequest[];
  recording?: Recording;
  recordingRequested?: boolean;
  recordingStatus?: string;
  automaticLEFailedScore?: AutomaticLefailedScore;
  isPaused?: boolean;
  questionResponses?: QuestionResponse[];
  monetized?: boolean;
  freeFormQuestionResponses?: FreeFormQuestionResponse[];
  multipleChoiceQuestionResponses?: MultipleChoiceQuestionResponse[];
  inReviewQuestionList?: InReviewQuestionList;
  latestHelpRequestedFor?: string;
  helpRequestedMessages?: HelpRequestMessage[];
  seekerHireDetailId?: string;
  statusAssignedBy?: { recruiterId?: string };
  offerAcceptanceStatus?: OfferAcceptanceStatus;
  latestHelpRequestedLabel?: Record<string, string>;
  hireFormTemplate?: HireFormTemplate;
  hireFormTemplateId?: string;
  isSeekerPreviouslyMonetized?: boolean;
}

export interface JobTitle {
  en: string;
  fr: string;
  es: string;
}

export interface ComputedStatus {
  category: string;
  lastUpdate: string;
  prioritySortValue: number;
  status: string;
}

export interface HireDetailsResubmissionRequest {
  field: string;
  comment: string;
}

export interface Recording {
  url: string;
  phrase: string;
  language: string;
  metadata: Metadata;
  phraseId: string;
  createdAt: string;
}

export interface Metadata {
  mimeType: string;
  sampleRate: number;
  channels: number;
  bitsPerSample: number;
}

export interface AutomaticLefailedScore {
  accuracy: number;
  fluency: number;
  language: string;
  phraseId: string;
  pronunciation: number;
  confidence: number;
  completeness: number;
  failedAt: string;
}

export interface QuestionResponse {
  id: string;
  knockout: boolean;
  preferredAnswer: boolean;
  text: string;
  isPausable: boolean;
  answer: boolean;
  reviewed: boolean;
}

export interface FreeFormQuestionResponse {
  id: string;
  text: string;
  isPausable: boolean;
  answer: string;
  reviewed: boolean;
}

export interface MultipleChoiceQuestionResponse {
  answers: Answer[];
  isAnsweredCorrectly: boolean;
  id: string;
  knockout: boolean;
  question: string;
  options: Option[];
  preferredAnswers: string[];
  totalAnswer: number;
  correctAnswerDeterminationType: string;
  reviewed: boolean;
  isPausable: boolean;
  images: Image[];
}

export interface Answer {
  id: string;
  answer: string;
}

export interface Option {
  id: string;
  text: string;
  wasAnswered: boolean;
}

export interface Image {
  galleryId: string;
  url: string;
  path: string;
}

export interface InReviewQuestionList {
  questionResponses: QuestionResponse2[];
  multipleChoiceQuestionResponses: MultipleChoiceQuestionResponse2[];
  freeFormQuestionResponses: FreeFormQuestionResponse2[];
}

export interface QuestionResponse2 {
  id: string;
  knockout: boolean;
  preferredAnswer: boolean;
  text: string;
  isPausable: boolean;
  answer: boolean;
  reviewed: boolean;
}

export interface MultipleChoiceQuestionResponse2 {
  answers: Answer2[];
  isAnsweredCorrectly: boolean;
  id: string;
  knockout: boolean;
  question: string;
  options: Option2[];
  preferredAnswers: string[];
  totalAnswer: number;
  correctAnswerDeterminationType: string;
  reviewed: boolean;
  isPausable: boolean;
  images: Image2[];
}

export interface Answer2 {
  id: string;
  answer: string;
}

export interface Option2 {
  id: string;
  text: string;
  wasAnswered: boolean;
}

export interface Image2 {
  galleryId: string;
  url: string;
  path: string;
}

export interface FreeFormQuestionResponse2 {
  id: string;
  text: string;
  isPausable: boolean;
  answer: string;
  reviewed: boolean;
}

export enum TodoActionType {
  BACKGROUND_CHECK,
  HIRE_FORM,
  OFFER,
  LANGUAGE_EVALUATION,
  CONVERSATIONAL_AI,
  OTHER,
  I9_VERIFICATION,
  REVIEW_ANSWERS,
  OFFER_REJECTED,
  OFFER_EXPIRED,
  OFFER_ACCEPTED,
  UPCOMING_INTERVIEWS,
}

export type HelpRequestMessage = {
  userType: 'seeker' | 'recruiter';
  message: string;
  timestamp: Date;
  name: string;
};

export type OfferAcceptanceStatus = {
  offerAccepted: boolean;
  reason: string;
  customReason: string;
};
export type HireFormTemplate = {
  defaultLanguage: string;
  languages: string[];
};

export type FormattedTodo = {
  id: string;
  actionType: TodoActionType;
  appointment?: Appointment;
  title: string;
  description: string;
  lastStatus?: string;
  status: string;
  location: string;
  position: string;
  candidateFullName: string;
  candidateId: string;
  customName: string;
  todoStatus: StatusEnum;
  category: string;
  helpRequestedMessages?: HelpRequestMessage[];
  latestHelpRequestedFor?: string;
  hireDetailsRequestedAt?: string;
  seekerHireDetailId?: string;
  statusAssignedBy?: {
    recruiterName: string;
    recruiterId: string;
  };
  statusAssignedRecruiter: string;
  inReviewQuestionList?: InReviewQuestionList;
  monetized?: boolean;
  offerAcceptanceStatus?: OfferAcceptanceStatus;
  latestHelpRequestedLabel?: Record<string, string>;
  hireFormTemplate?: HireFormTemplate;
  hireFormTemplateId?: string;
  isSeekerPreviouslyMonetized?: boolean;
};

export enum StatusOptions {
  TODO = 'To Do',
  IN_PROGRESS_ME = 'In Progress (for me)',
  IN_PROGRESS_OTHERS = 'In Progress (for others)',
  COMPLETED = 'Completed',
}
export enum StatusEnum {
  TODO = 'TODO',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
}
